/**
 * Sistema de permissões por bitfield.
 *
 * Guardamos como BigInt em memória e como string decimal no banco e no JSON,
 * porque JSON não representa inteiros de 64 bits sem perda.
 */
export const Permission = {
    VIEW_CHANNEL: 1n << 0n,
    SEND_MESSAGES: 1n << 1n,
    MANAGE_MESSAGES: 1n << 2n,
    EMBED_LINKS: 1n << 3n,
    ATTACH_FILES: 1n << 4n,
    ADD_REACTIONS: 1n << 5n,
    MENTION_EVERYONE: 1n << 6n,
    READ_MESSAGE_HISTORY: 1n << 7n,
    CONNECT: 1n << 8n,
    SPEAK: 1n << 9n,
    STREAM_VIDEO: 1n << 10n,
    SHARE_SCREEN: 1n << 11n,
    USE_VOICE_ACTIVITY: 1n << 12n,
    PRIORITY_SPEAKER: 1n << 13n,
    MUTE_MEMBERS: 1n << 14n,
    DEAFEN_MEMBERS: 1n << 15n,
    MOVE_MEMBERS: 1n << 16n,
    CREATE_INVITE: 1n << 17n,
    CHANGE_NICKNAME: 1n << 18n,
    MANAGE_NICKNAMES: 1n << 19n,
    KICK_MEMBERS: 1n << 20n,
    BAN_MEMBERS: 1n << 21n,
    MANAGE_CHANNELS: 1n << 22n,
    MANAGE_ROLES: 1n << 23n,
    MANAGE_GUILD: 1n << 24n,
    VIEW_AUDIT_LOG: 1n << 25n,
    ADMINISTRATOR: 1n << 30n,
};
/** Rótulos em pt-BR para a UI de configuração de cargos. */
export const PERMISSION_LABELS = {
    VIEW_CHANNEL: 'Ver canais',
    SEND_MESSAGES: 'Enviar mensagens',
    MANAGE_MESSAGES: 'Gerenciar mensagens',
    EMBED_LINKS: 'Incorporar links',
    ATTACH_FILES: 'Anexar arquivos',
    ADD_REACTIONS: 'Adicionar reações',
    MENTION_EVERYONE: 'Mencionar @everyone',
    READ_MESSAGE_HISTORY: 'Ver histórico de mensagens',
    CONNECT: 'Conectar em voz',
    SPEAK: 'Falar',
    STREAM_VIDEO: 'Transmitir câmera',
    SHARE_SCREEN: 'Compartilhar tela',
    USE_VOICE_ACTIVITY: 'Usar detecção de voz',
    PRIORITY_SPEAKER: 'Locutor prioritário',
    MUTE_MEMBERS: 'Silenciar membros',
    DEAFEN_MEMBERS: 'Ensurdecer membros',
    MOVE_MEMBERS: 'Mover membros',
    CREATE_INVITE: 'Criar convite',
    CHANGE_NICKNAME: 'Mudar o próprio apelido',
    MANAGE_NICKNAMES: 'Gerenciar apelidos',
    KICK_MEMBERS: 'Expulsar membros',
    BAN_MEMBERS: 'Banir membros',
    MANAGE_CHANNELS: 'Gerenciar canais',
    MANAGE_ROLES: 'Gerenciar cargos',
    MANAGE_GUILD: 'Gerenciar servidor',
    VIEW_AUDIT_LOG: 'Ver registro de auditoria',
    ADMINISTRATOR: 'Administrador',
};
/** Agrupamento usado só para organizar a tela de permissões. */
export const PERMISSION_GROUPS = [
    {
        label: 'Geral',
        keys: ['VIEW_CHANNEL', 'CREATE_INVITE', 'CHANGE_NICKNAME', 'VIEW_AUDIT_LOG'],
    },
    {
        label: 'Texto',
        keys: [
            'SEND_MESSAGES',
            'EMBED_LINKS',
            'ATTACH_FILES',
            'ADD_REACTIONS',
            'READ_MESSAGE_HISTORY',
            'MENTION_EVERYONE',
            'MANAGE_MESSAGES',
        ],
    },
    {
        label: 'Voz e vídeo',
        keys: [
            'CONNECT',
            'SPEAK',
            'STREAM_VIDEO',
            'SHARE_SCREEN',
            'USE_VOICE_ACTIVITY',
            'PRIORITY_SPEAKER',
        ],
    },
    {
        label: 'Moderação',
        keys: [
            'MUTE_MEMBERS',
            'DEAFEN_MEMBERS',
            'MOVE_MEMBERS',
            'MANAGE_NICKNAMES',
            'KICK_MEMBERS',
            'BAN_MEMBERS',
        ],
    },
    {
        label: 'Administração',
        keys: ['MANAGE_CHANNELS', 'MANAGE_ROLES', 'MANAGE_GUILD', 'ADMINISTRATOR'],
    },
];
/** Permissões do cargo @everyone recém-criado. */
export const DEFAULT_EVERYONE_PERMISSIONS = Permission.VIEW_CHANNEL |
    Permission.SEND_MESSAGES |
    Permission.EMBED_LINKS |
    Permission.ATTACH_FILES |
    Permission.ADD_REACTIONS |
    Permission.READ_MESSAGE_HISTORY |
    Permission.CONNECT |
    Permission.SPEAK |
    Permission.STREAM_VIDEO |
    Permission.SHARE_SCREEN |
    Permission.USE_VOICE_ACTIVITY |
    Permission.CHANGE_NICKNAME |
    Permission.CREATE_INVITE;
export const ALL_PERMISSIONS = Object.values(Permission).reduce((acc, p) => acc | p, 0n);
export function toBitString(bits) {
    return bits.toString();
}
export function fromBitString(value) {
    if (!value)
        return 0n;
    try {
        return BigInt(value);
    }
    catch {
        return 0n;
    }
}
/**
 * Um dono de servidor ou portador de ADMINISTRATOR passa em qualquer checagem.
 * Fora isso, é um teste de bit simples.
 */
export function hasPermission(bits, permission) {
    if ((bits & Permission.ADMINISTRATOR) === Permission.ADMINISTRATOR)
        return true;
    return (bits & permission) === permission;
}
export function hasAnyPermission(bits, permissions) {
    return permissions.some((p) => hasPermission(bits, p));
}
export function computeChannelPermissions(params) {
    const { isOwner, everyoneRoleId, memberRoleIds, rolePermissions, overwrites, memberId } = params;
    if (isOwner)
        return ALL_PERMISSIONS;
    let base = rolePermissions.get(everyoneRoleId) ?? 0n;
    for (const roleId of memberRoleIds) {
        base |= rolePermissions.get(roleId) ?? 0n;
    }
    if ((base & Permission.ADMINISTRATOR) === Permission.ADMINISTRATOR) {
        return ALL_PERMISSIONS;
    }
    const everyoneOverwrite = overwrites.find((o) => o.targetType === 'role' && o.targetId === everyoneRoleId);
    if (everyoneOverwrite) {
        base &= ~everyoneOverwrite.deny;
        base |= everyoneOverwrite.allow;
    }
    let roleAllow = 0n;
    let roleDeny = 0n;
    for (const roleId of memberRoleIds) {
        const ow = overwrites.find((o) => o.targetType === 'role' && o.targetId === roleId);
        if (ow) {
            roleAllow |= ow.allow;
            roleDeny |= ow.deny;
        }
    }
    base &= ~roleDeny;
    base |= roleAllow;
    const memberOverwrite = overwrites.find((o) => o.targetType === 'member' && o.targetId === memberId);
    if (memberOverwrite) {
        base &= ~memberOverwrite.deny;
        base |= memberOverwrite.allow;
    }
    return base;
}
//# sourceMappingURL=permissions.js.map